import Phaser from 'phaser';
import { SCENES } from '../config/constants';
import { BUILD_PALETTE, BUILDINGS } from '../data/buildings';
import { HIREABLE_STAFF, STAFF_TYPES } from '../data/staff';
import { UPGRADE_LIST, UPGRADES } from '../data/upgrades';
import { VEHICLE_TYPES } from '../data/vehicles';
import type { ParkingLot } from '../models/ParkingLot';
import { TILE_COLORS, type TileType } from '../models/Tile';
import { VEHICLE_STATE } from '../models/Vehicle';
import { gameStore } from '../simulation/GameStore';
import { EconomyEngine } from '../simulation/EconomyEngine';
import { ParkingLotEngine } from '../simulation/ParkingLotEngine';
import { ReputationEngine } from '../simulation/ReputationEngine';
import { UpgradeEngine } from '../simulation/UpgradeEngine';
import { VehicleEngine } from '../simulation/VehicleEngine';
import { formatCurrency } from '../utils/formatCurrency';
import { fontPx, px } from '../ui/scale';
import { t, tBuilding, tStaff, tUpgrade } from '../ui/i18n';

const ERASER = 'eraser';

/** A rendered car: a container (body + windshield) plus eased render state. */
interface CarView {
  container: Phaser.GameObjects.Container;
  rx: number; // current render x (px), eased toward the logical position
  ry: number;
  angle: number; // current heading in radians
  lastState: string; // to detect the moment of payment
}

/** A walking person (driver or staff) — purely visual (not in the sim/save). */
interface Ped {
  container: Phaser.GameObjects.Container;
  x: number; // tile coords (float)
  y: number;
  tx: number; // target tile
  ty: number;
  kind: string; // 'driver' | staff typeId
  speed: number; // tiles per real second
  idleUntilMs: number;
  carId?: string; // for drivers: the car they belong to
  destX?: number; // driver's out-of-car destination
  destY?: number;
  homeX: number; // anchor tile (booth for cashier, etc.)
  homeY: number;
  goingOut?: boolean; // driver toggle: heading to destination vs back to car
}

const PED_COLORS: Record<string, number> = {
  driver: 0xeceff1,
  cashier: 0xffca28,
  cleaner: 0x66bb6a,
  security: 0x42a5f5,
  manager: 0xab47bc,
  technician: 0xff9800,
};

/**
 * ParkingLotScene (spec sections 8 & 28): the grid editor where the player
 * builds a parking lot. Select a tool from the palette, then click/drag on the
 * grid to place tiles (cash is deducted, overlaps and unaffordable placements
 * are rejected). The eraser clears tiles.
 *
 * All validation/money handling is in the engine/store; this scene only renders
 * and forwards intent.
 */
export class ParkingLotScene extends Phaser.Scene {
  private lotId!: string;
  private tileRects: Phaser.GameObjects.Rectangle[] = [];
  private gridOriginX = 0;
  private gridOriginY = 0;
  private tileSize = 0;
  private selectedTool: string = 'parking';
  private isPainting = false;

  private infoText!: Phaser.GameObjects.Text;
  private priceText!: Phaser.GameObjects.Text;
  private hoverRect!: Phaser.GameObjects.Rectangle;
  private paletteButtons = new Map<string, Phaser.GameObjects.Rectangle>();
  private staffCountTexts = new Map<string, Phaser.GameObjects.Text>();
  private staffSummaryText?: Phaser.GameObjects.Text;
  private vehicleSprites = new Map<string, CarView>();
  private vehicleLayer!: Phaser.GameObjects.Container;
  private pedLayer!: Phaser.GameObjects.Container;
  private peds = new Map<string, Ped>();
  /** Parking-space tiles currently tinted by parking duration (to reset next frame). */
  private tintedSpaces = new Set<number>();
  private upgradesPanel?: Phaser.GameObjects.Container;
  private unsubscribe?: () => void;

  constructor() {
    super(SCENES.PARKING_LOT);
  }

  init(data: { lotId?: string }): void {
    this.lotId = data.lotId ?? '';
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#0e1622');

    if (!this.scene.isActive(SCENES.UI)) {
      this.scene.launch(SCENES.UI);
    }

    const lot = gameStore.getLot(this.lotId);
    if (!lot) {
      // Nothing to edit — return to the city.
      this.scene.start(SCENES.CITY);
      return;
    }

    this.tileRects = [];
    this.paletteButtons.clear();
    this.staffCountTexts.clear();
    this.vehicleSprites.clear();
    this.peds.clear();

    this.computeGridMetrics(lot);
    this.drawGrid(lot);
    this.vehicleLayer = this.add.container(0, 0);
    this.pedLayer = this.add.container(0, 0);
    this.drawHoverRect();
    this.drawPalette();
    this.drawStaffPanel();
    this.drawHeader(lot);
    this.drawPriceControls();
    this.bindInput(lot);

    this.unsubscribe = gameStore.subscribe(() => this.refresh());

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribe?.();
      this.unsubscribe = undefined;
    });
  }

  // --- layout ---

  private computeGridMetrics(lot: ParkingLot): void {
    const areaX = px(240);
    const areaY = px(160);
    const areaW = this.scale.width - areaX - px(24);
    const areaH = this.scale.height - areaY - px(24);

    this.tileSize = Math.floor(
      Math.min(areaW / lot.width, areaH / lot.height)
    );

    const gridW = this.tileSize * lot.width;
    const gridH = this.tileSize * lot.height;
    this.gridOriginX = areaX + (areaW - gridW) / 2;
    this.gridOriginY = areaY + (areaH - gridH) / 2;
  }

  private drawGrid(lot: ParkingLot): void {
    for (let y = 0; y < lot.height; y++) {
      for (let x = 0; x < lot.width; x++) {
        const idx = ParkingLotEngine.index(lot.width, x, y);
        const tile = lot.tiles[idx];
        const rect = this.add
          .rectangle(
            this.gridOriginX + x * this.tileSize + this.tileSize / 2,
            this.gridOriginY + y * this.tileSize + this.tileSize / 2,
            this.tileSize - 1,
            this.tileSize - 1,
            TILE_COLORS[tile]
          )
          .setStrokeStyle(1, 0x000000, 0.15);
        this.tileRects[idx] = rect;
      }
    }
  }

  private drawHoverRect(): void {
    this.hoverRect = this.add
      .rectangle(0, 0, this.tileSize, this.tileSize, 0xffffff, 0.15)
      .setStrokeStyle(px(2), 0xffffff, 0.8)
      .setVisible(false);
  }

  private drawHeader(lot: ParkingLot): void {
    // Leave room at the very top for the persistent HUD overlay bar.
    this.add
      .text(px(240), px(72), lot.name, {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(24),
        color: '#eceff1',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);

    this.infoText = this.add
      .text(px(240), px(100), '', {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(14),
        color: '#b0bec5',
      })
      .setOrigin(0, 0.5);

    // Back to city button (top-right, below the HUD bar).
    const backW = px(150);
    const backH = px(40);
    const bx = this.scale.width - px(24) - backW / 2;
    const by = px(72);
    const back = this.add
      .rectangle(bx, by, backW, backH, 0x37474f)
      .setStrokeStyle(px(2), 0x90a4ae)
      .setInteractive({ useHandCursor: true });
    this.add
      .text(bx, by, t('lot.back'), {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(15),
        color: '#eceff1',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    back.on('pointerup', () => this.scene.start(SCENES.CITY));

    // Upgrades toggle button (left of Back).
    const upW = px(150);
    const ux = bx - backW / 2 - px(12) - upW / 2;
    const up = this.add
      .rectangle(ux, by, upW, backH, 0x4527a0)
      .setStrokeStyle(px(2), 0x9575cd)
      .setInteractive({ useHandCursor: true });
    this.add
      .text(ux, by, t('lot.upgrades'), {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(15),
        color: '#ede7f6',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    up.on('pointerup', () => this.toggleUpgrades());

    // Expand-lot button (left of Upgrades).
    const info = gameStore.getExpansionInfo(this.lotId);
    if (info) {
      const exW = px(180);
      const ex = ux - upW / 2 - px(12) - exW / 2;
      const enabled = !info.atMax;
      const eb = this.add
        .rectangle(ex, by, exW, backH, enabled ? 0x00695c : 0x37474f)
        .setStrokeStyle(px(2), enabled ? 0x4db6ac : 0x546e7a)
        .setInteractive({ useHandCursor: true });
      const eLabel = info.atMax
        ? t('lot.maxSize')
        : t('lot.expand', { w: info.nextWidth, h: info.nextHeight });
      this.add
        .text(ex, by, eLabel, {
          fontFamily: 'Segoe UI, Tahoma, sans-serif',
          fontSize: fontPx(13),
          color: enabled ? '#e0f2f1' : '#78909c',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      if (enabled) {
        eb.on('pointerup', () => {
          const res = gameStore.expandLot(this.lotId);
          if (res.ok) this.scene.restart({ lotId: this.lotId });
          else if (res.reason === 'insufficient-funds')
            this.flashInfo(t('lot.noCashExpand'));
        });
      }
    }

    this.refresh();
  }

  private toggleUpgrades(): void {
    if (this.upgradesPanel) {
      this.upgradesPanel.destroy();
      this.upgradesPanel = undefined;
      return;
    }
    this.buildUpgradesOverlay();
  }

  private buildUpgradesOverlay(): void {
    const lot = gameStore.getLot(this.lotId);
    if (!lot) return;

    const panel = this.add.container(0, 0).setDepth(100);
    const { width, height } = this.scale;
    const pw = px(620);
    const ph = px(70) + UPGRADE_LIST.length * px(64) + px(20);
    const cx = width / 2;
    const top = height / 2 - ph / 2;

    // Dim background (click to close).
    const dim = this.add
      .rectangle(0, 0, width, height, 0x000000, 0.55)
      .setOrigin(0)
      .setInteractive();
    dim.on('pointerup', () => this.toggleUpgrades());
    panel.add(dim);

    const card = this.add
      .rectangle(cx, height / 2, pw, ph, 0x141c2b)
      .setStrokeStyle(px(2), 0x5c6bc0);
    panel.add(card);

    panel.add(
      this.add
        .text(cx, top + px(28), t('lot.upgrades'), {
          fontFamily: 'Segoe UI, Tahoma, sans-serif',
          fontSize: fontPx(22),
          color: '#eceff1',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
    );

    const cashText = this.add
      .text(cx + pw / 2 - px(20), top + px(28), formatCurrency(gameStore.getState().player.cash), {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(15),
        color: '#ffd54f',
      })
      .setOrigin(1, 0.5);
    panel.add(cashText);

    const leftX = cx - pw / 2 + px(24);
    let y = top + px(70);

    for (const id of UPGRADE_LIST) {
      const def = UPGRADES[id];
      const owned = UpgradeEngine.owns(lot, id);
      const check = UpgradeEngine.canPurchase(lot, def, gameStore.getState().player.cash);

      panel.add(
        this.add
          .text(leftX, y, tUpgrade(def.id, def.name), {
            fontFamily: 'Segoe UI, Tahoma, sans-serif',
            fontSize: fontPx(16),
            color: '#eceff1',
            fontStyle: 'bold',
          })
          .setOrigin(0, 0.5)
      );
      panel.add(
        this.add
          .text(leftX, y + px(18), `${def.description}  •  maint ฿${def.maintenanceCost}/mo`, {
            fontFamily: 'Segoe UI, Tahoma, sans-serif',
            fontSize: fontPx(12),
            color: '#90a4ae',
          })
          .setOrigin(0, 0.5)
      );

      const btnX = cx + pw / 2 - px(24);
      if (owned) {
        panel.add(
          this.add
            .text(btnX, y, t('lot.marketOwned'), {
              fontFamily: 'Segoe UI, Tahoma, sans-serif',
              fontSize: fontPx(15),
              color: '#a5d6a7',
              fontStyle: 'bold',
            })
            .setOrigin(1, 0.5)
        );
      } else if (!check.ok && check.reason === 'missing-prerequisite') {
        const need = def.prerequisites
          .map((p) => tUpgrade(p, UPGRADES[p]?.name ?? p))
          .join(', ');
        panel.add(
          this.add
            .text(btnX, y, t('lot.needs', { v: need }), {
              fontFamily: 'Segoe UI, Tahoma, sans-serif',
              fontSize: fontPx(12),
              color: '#b0bec5',
            })
            .setOrigin(1, 0.5)
        );
      } else {
        const affordable = check.ok;
        const bw = px(130);
        const bh = px(34);
        const btn = this.add
          .rectangle(btnX - bw / 2, y, bw, bh, affordable ? 0x1e88e5 : 0x37474f)
          .setStrokeStyle(px(2), affordable ? 0x64b5f6 : 0x455a64)
          .setInteractive({ useHandCursor: true });
        panel.add(btn);
        panel.add(
          this.add
            .text(btnX - bw / 2, y, t('lot.buy', { v: formatCurrency(def.cost) }), {
              fontFamily: 'Segoe UI, Tahoma, sans-serif',
              fontSize: fontPx(13),
              color: affordable ? '#ffffff' : '#78909c',
              fontStyle: 'bold',
            })
            .setOrigin(0.5)
        );
        btn.on('pointerup', () => {
          const res = gameStore.purchaseUpgrade(this.lotId, id);
          if (res.ok) {
            // Rebuild overlay to reflect new ownership / unlocked items.
            this.upgradesPanel?.destroy();
            this.upgradesPanel = undefined;
            this.buildUpgradesOverlay();
            this.refresh();
          }
        });
      }

      y += px(64);
    }

    this.upgradesPanel = panel;
  }

  private drawPriceControls(): void {
    const y = px(128);
    let x = px(240);

    this.add
      .text(x, y, t('lot.price'), {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(14),
        color: '#78909c',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);
    x += px(52);

    const mkBtn = (label: string, dir: 1 | -1): number => {
      const size = px(26);
      const btn = this.add
        .rectangle(x, y, size, size, 0x1a2333)
        .setStrokeStyle(px(2), 0x64b5f6)
        .setInteractive({ useHandCursor: true });
      this.add
        .text(x, y, label, {
          fontFamily: 'Segoe UI, Tahoma, sans-serif',
          fontSize: fontPx(18),
          color: '#eceff1',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      btn.on('pointerup', () => {
        gameStore.adjustLotPrice(this.lotId, dir);
        this.refresh();
      });
      return x + size + px(6);
    };

    x = mkBtn('−', -1);
    this.priceText = this.add
      .text(x, y, '', {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(16),
        color: '#ffd54f',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);
    x += px(96);
    mkBtn('+', 1);
  }

  private drawPalette(): void {
    const panelX = px(20);
    const panelW = px(200);
    let y = px(96);

    this.add
      .text(panelX, y, t('lot.buildTools'), {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(14),
        color: '#78909c',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);
    y += px(30);

    const rowH = px(52);
    for (const id of BUILD_PALETTE) {
      const def = BUILDINGS[id];
      this.drawPaletteButton(
        id,
        tBuilding(def.id, def.name),
        formatCurrency(def.purchaseCost),
        TILE_COLORS[def.tile],
        panelX,
        y,
        panelW,
        rowH
      );
      y += rowH + px(8);
    }

    // Eraser tool.
    this.drawPaletteButton(
      ERASER,
      t('lot.eraser'),
      t('lot.free'),
      0x263238,
      panelX,
      y,
      panelW,
      rowH
    );

    this.updatePaletteSelection();
  }

  private drawPaletteButton(
    id: string,
    name: string,
    cost: string,
    swatch: number,
    x: number,
    y: number,
    w: number,
    h: number
  ): void {
    const btn = this.add
      .rectangle(x, y, w, h, 0x1a2333, 1)
      .setOrigin(0, 0.5)
      .setStrokeStyle(px(2), 0x37474f)
      .setInteractive({ useHandCursor: true });

    // Color swatch.
    this.add
      .rectangle(x + px(8), y, px(20), px(20), swatch)
      .setOrigin(0, 0.5)
      .setStrokeStyle(1, 0x000000, 0.3);

    this.add
      .text(x + px(38), y - px(9), name, {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(15),
        color: '#eceff1',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);
    this.add
      .text(x + px(38), y + px(10), cost, {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(12),
        color: '#90a4ae',
      })
      .setOrigin(0, 0.5);

    btn.on('pointerup', () => {
      this.selectedTool = id;
      this.updatePaletteSelection();
      this.refresh();
    });

    this.paletteButtons.set(id, btn);
  }

  private drawStaffPanel(): void {
    const panelX = px(20);
    // Start below the build palette (label + 5 tools + eraser).
    const paletteBottom =
      px(96) + px(30) + (BUILD_PALETTE.length + 1) * (px(52) + px(8));
    let y = paletteBottom + px(16);

    this.add
      .text(panelX, y, t('lot.staff'), {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(14),
        color: '#78909c',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);
    y += px(22);

    // Live effect summary so the impact of hiring is immediately visible.
    this.staffSummaryText = this.add
      .text(panelX, y, '', {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(11),
        color: '#90a4ae',
        wordWrap: { width: px(200) },
        lineSpacing: px(2),
      })
      .setOrigin(0, 0);
    y += px(48);

    const rowH = px(40);
    for (const id of HIREABLE_STAFF) {
      const def = STAFF_TYPES[id];
      this.add
        .text(panelX, y - px(8), tStaff(def.id), {
          fontFamily: 'Segoe UI, Tahoma, sans-serif',
          fontSize: fontPx(14),
          color: '#eceff1',
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5);
      this.add
        .text(panelX, y + px(9), `฿${Math.round(def.salary / 1000)}k/mo`, {
          fontFamily: 'Segoe UI, Tahoma, sans-serif',
          fontSize: fontPx(11),
          color: '#90a4ae',
        })
        .setOrigin(0, 0.5);

      const countText = this.add
        .text(panelX + px(120), y, '0', {
          fontFamily: 'Segoe UI, Tahoma, sans-serif',
          fontSize: fontPx(15),
          color: '#ffd54f',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      this.staffCountTexts.set(id, countText);

      this.miniButton(panelX + px(146), y, '−', () => {
        gameStore.fireStaff(this.lotId, id);
        this.refresh();
      });
      this.miniButton(panelX + px(176), y, '+', () => {
        gameStore.hireStaff(this.lotId, id);
        this.refresh();
      });

      y += rowH;
    }
  }

  private miniButton(
    x: number,
    y: number,
    label: string,
    onClick: () => void
  ): void {
    const size = px(24);
    const btn = this.add
      .rectangle(x, y, size, size, 0x1a2333)
      .setStrokeStyle(px(2), 0x64b5f6)
      .setInteractive({ useHandCursor: true });
    this.add
      .text(x, y, label, {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(16),
        color: '#eceff1',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    btn.on('pointerup', onClick);
  }

  private updatePaletteSelection(): void {
    for (const [id, btn] of this.paletteButtons) {
      const selected = id === this.selectedTool;
      btn.setStrokeStyle(px(2), selected ? 0x64b5f6 : 0x37474f);
      btn.setFillStyle(selected ? 0x243447 : 0x1a2333);
    }
  }

  // --- input ---

  private bindInput(lot: ParkingLot): void {
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      const cell = this.worldToTile(lot, p.worldX, p.worldY);
      if (!cell) return;
      this.isPainting = true;
      this.applyTool(cell.x, cell.y);
    });

    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      const cell = this.worldToTile(lot, p.worldX, p.worldY);
      if (cell) {
        this.hoverRect
          .setPosition(
            this.gridOriginX + cell.x * this.tileSize + this.tileSize / 2,
            this.gridOriginY + cell.y * this.tileSize + this.tileSize / 2
          )
          .setVisible(true);
        if (this.isPainting) this.applyTool(cell.x, cell.y);
      } else {
        this.hoverRect.setVisible(false);
      }
    });

    this.input.on('pointerup', () => {
      this.isPainting = false;
    });
    this.input.on('gameout', () => {
      this.isPainting = false;
      this.hoverRect.setVisible(false);
    });
  }

  private worldToTile(
    lot: ParkingLot,
    worldX: number,
    worldY: number
  ): { x: number; y: number } | null {
    const x = Math.floor((worldX - this.gridOriginX) / this.tileSize);
    const y = Math.floor((worldY - this.gridOriginY) / this.tileSize);
    if (x < 0 || y < 0 || x >= lot.width || y >= lot.height) return null;
    return { x, y };
  }

  private applyTool(x: number, y: number): void {
    if (this.selectedTool === ERASER) {
      gameStore.removeTile(this.lotId, x, y);
      return;
    }
    const result = gameStore.placeTile(this.lotId, x, y, this.selectedTool);
    if (!result.ok && result.reason === 'insufficient-funds') {
      this.isPainting = false; // stop drag-spamming failures
      this.flashInfo(t('lot.noCashBuild'));
    }
  }

  // --- render sync ---

  private refresh(): void {
    const lot = gameStore.getLot(this.lotId);
    if (!lot) return;

    // Sync tile colors from the (possibly changed) grid.
    for (let i = 0; i < lot.tiles.length; i++) {
      const rect = this.tileRects[i];
      if (rect) rect.setFillStyle(TILE_COLORS[lot.tiles[i] as TileType]);
    }

    const capacity = ParkingLotEngine.getCapacity(lot);
    const toolLabel =
      this.selectedTool === ERASER
        ? 'Eraser'
        : BUILDINGS[this.selectedTool]?.name ?? '';
    const demand = Math.round(gameStore.getDemandEstimate(this.lotId));
    this.infoText.setText(
      `Occupancy: ${lot.occupiedSpaces}/${capacity}   Entrance: ${
        ParkingLotEngine.hasEntrance(lot) ? '✓' : '—'
      }   Exit: ${ParkingLotEngine.hasExit(lot) ? '✓' : '—'}   •   ` +
        `Demand: ~${demand}/hr   Served: ${lot.vehiclesServed}   Rejected: ${lot.vehiclesRejected}   •   ` +
        `Clean ${Math.round(lot.cleanliness)}  Sec ${Math.round(
          lot.security
        )}  Wait ${lot.avgWaitMinutes.toFixed(1)}m   •   ` +
        `Rep ${Math.round(lot.reputation)} (${ReputationEngine.rating(
          lot.reputation
        )})  Sat ${Math.round(lot.customerSatisfaction)}   •   Tool: ${toolLabel}`
    );

    if (this.priceText) {
      this.priceText.setText(`฿${lot.parkingPrice}/hr`);
    }

    // Live staff/quality summary so hiring shows an immediate effect.
    if (this.staffSummaryText) {
      const canAdmit = gameStore.canAdmitEntry(this.lotId);
      const canCollect = gameStore.canCollectPayment(this.lotId);
      const lanes = VehicleEngine.paymentLanes(lot);
      const queueLen = lot.vehicles.filter(
        (v) => v.state === VEHICLE_STATE.Paying
      ).length;
      // Entry blocked is the more serious problem (no cars at all), so it wins.
      const warn = !canAdmit
        ? t('lot.noEntry')
        : canCollect
          ? t('lot.collecting', { lanes, q: queueLen })
          : t('lot.noCashier');
      this.staffSummaryText.setText(
        `Cleanliness ${Math.round(lot.cleanliness)}  •  Security ${Math.round(
          lot.security
        )}\n${warn}`
      );
      this.staffSummaryText.setColor(canAdmit && canCollect ? '#90a4ae' : '#ff8a80');
    }

    // Sync staff counts.
    for (const [id, text] of this.staffCountTexts) {
      const count = lot.staff.reduce(
        (n, s) => (s.typeId === id ? n + 1 : n),
        0
      );
      text.setText(String(count));
    }
  }

  /** Build a little top-down car: body + windshield, pointing +x by default. */
  private createCarSprite(color: number): Phaser.GameObjects.Container {
    const len = Math.max(6, this.tileSize * 0.72);
    const wid = Math.max(4, this.tileSize * 0.42);

    const body = this.add
      .rectangle(0, 0, len, wid, color)
      .setStrokeStyle(Math.max(1, this.tileSize * 0.03), 0x0a0a0a, 0.6);
    // Windshield near the front (+x side).
    const glass = this.add.rectangle(len * 0.18, 0, len * 0.28, wid * 0.7, 0x1a1a1a, 0.55);
    // Headlights hint (front edge).
    const light = this.add.rectangle(len * 0.46, 0, len * 0.06, wid * 0.8, 0xfff59d, 0.9);

    const c = this.add.container(0, 0, [body, glass, light]);
    this.vehicleLayer.add(c);
    return c;
  }

  /**
   * Phaser render loop: draw vehicles at their current grid positions with
   * smooth interpolation and heading, so movement reads like real cars rather
   * than dots teleporting between sim ticks. `delta` is real ms this frame.
   */
  update(_time: number, delta: number): void {
    const lot = gameStore.getLot(this.lotId);
    if (!lot || !this.vehicleLayer) return;

    const seen = new Set<string>();
    // Easing factor: ~140ms time-constant, framerate independent.
    const ease = Math.min(1, delta / 140);

    for (const v of lot.vehicles) {
      seen.add(v.id);
      const tx = this.gridOriginX + v.x * this.tileSize + this.tileSize / 2;
      const ty = this.gridOriginY + v.y * this.tileSize + this.tileSize / 2;

      let view = this.vehicleSprites.get(v.id);
      if (!view) {
        const color = VEHICLE_TYPES[v.typeId]?.color ?? 0xffffff;
        view = {
          container: this.createCarSprite(color),
          rx: tx,
          ry: ty,
          angle: 0,
          lastState: v.state,
        };
        this.vehicleSprites.set(v.id, view);
      }

      // Show a "฿fee" popup the moment a car starts paying (cashier/exit).
      if (view.lastState !== v.state && v.state === VEHICLE_STATE.Paying) {
        this.showPaymentPopup(tx, ty, EconomyEngine.computeParkingFee(lot, v));
      }
      view.lastState = v.state;

      // Ease the render position toward the logical position.
      const ndx = tx - view.rx;
      const ndy = ty - view.ry;
      view.rx += ndx * ease;
      view.ry += ndy * ease;

      // Point the car along its actual motion when it is moving enough.
      const moving = Math.hypot(ndx, ndy) > this.tileSize * 0.05;
      if (moving) {
        const target = Math.atan2(ndy, ndx);
        // Shortest-arc turn toward the target heading.
        let d = target - view.angle;
        while (d > Math.PI) d -= Math.PI * 2;
        while (d < -Math.PI) d += Math.PI * 2;
        view.angle += d * Math.min(1, ease * 1.5);
      }

      view.container.setPosition(view.rx, view.ry);
      view.container.setRotation(view.angle);
      view.container.setAlpha(v.state === VEHICLE_STATE.Parked ? 0.9 : 1);
    }

    // Remove sprites for vehicles that have despawned.
    for (const [id, view] of this.vehicleSprites) {
      if (!seen.has(id)) {
        view.container.destroy();
        this.vehicleSprites.delete(id);
      }
    }

    this.updatePedestrians(lot, delta);
    this.tintParkingByDuration(lot);
  }

  /** Tint occupied parking tiles by how long the car has been parked. */
  private tintParkingByDuration(lot: ParkingLot): void {
    // Reset tiles tinted last frame back to their base color.
    for (const idx of this.tintedSpaces) {
      const rect = this.tileRects[idx];
      if (rect) rect.setFillStyle(TILE_COLORS[lot.tiles[idx] as TileType]);
    }
    this.tintedSpaces.clear();

    for (const v of lot.vehicles) {
      if (v.state !== VEHICLE_STATE.Parked || v.targetSpace === null) continue;
      const rect = this.tileRects[v.targetSpace];
      if (!rect) continue;
      const planned = Math.max(1, v.plannedDurationMinutes);
      const elapsed = planned - v.parkedRemainingMinutes;
      const frac = Math.max(0, Math.min(1, elapsed / planned));
      rect.setFillStyle(this.durationColor(frac));
      this.tintedSpaces.add(v.targetSpace);
    }
  }

  /** Green (just parked) -> yellow -> red (about to leave). */
  private durationColor(frac: number): number {
    const green = { r: 0x66, g: 0xbb, b: 0x6a };
    const yellow = { r: 0xff, g: 0xee, b: 0x58 };
    const red = { r: 0xef, g: 0x53, b: 0x50 };
    const lerp = (a: number, b: number, t: number) => Math.round(a + (b - a) * t);
    let c1, c2, t;
    if (frac < 0.5) {
      c1 = green;
      c2 = yellow;
      t = frac / 0.5;
    } else {
      c1 = yellow;
      c2 = red;
      t = (frac - 0.5) / 0.5;
    }
    return (
      (lerp(c1.r, c2.r, t) << 16) | (lerp(c1.g, c2.g, t) << 8) | lerp(c1.b, c2.b, t)
    );
  }

  // --- pedestrians (visual only) ---

  private tileCenterPx(tx: number, ty: number): { px: number; py: number } {
    return {
      px: this.gridOriginX + tx * this.tileSize + this.tileSize / 2,
      py: this.gridOriginY + ty * this.tileSize + this.tileSize / 2,
    };
  }

  private createPed(kind: string, x: number, y: number): Ped {
    const color = PED_COLORS[kind] ?? 0xffffff;
    const r = Math.max(2, this.tileSize * 0.16);
    const body = this.add.circle(0, 0, r, color).setStrokeStyle(1, 0x000000, 0.5);
    const head = this.add.circle(0, -r * 0.7, r * 0.55, 0x3e2723, 0.9);
    const container = this.add.container(0, 0, [body, head]).setDepth(50);
    this.pedLayer.add(container);
    return {
      container,
      x,
      y,
      tx: x,
      ty: y,
      kind,
      speed: kind === 'driver' ? 1.8 : kind === 'manager' || kind === 'cashier' ? 0.8 : 1.3,
      idleUntilMs: 0,
      homeX: x,
      homeY: y,
    };
  }

  private randInt(max: number): number {
    return Math.floor(Math.random() * max);
  }

  private nearestTileOf(
    lot: ParkingLot,
    fromX: number,
    fromY: number,
    types: string[]
  ): { x: number; y: number } | null {
    let best: { x: number; y: number } | null = null;
    let bestD = Infinity;
    for (let i = 0; i < lot.tiles.length; i++) {
      if (!types.includes(lot.tiles[i])) continue;
      const x = i % lot.width;
      const y = Math.floor(i / lot.width);
      const d = Math.hypot(x - fromX, y - fromY);
      if (d < bestD) {
        bestD = d;
        best = { x, y };
      }
    }
    return best;
  }

  /** Booth/gate positions where cashiers stand (cashier > machine > exit). */
  private cashierStations(lot: ParkingLot): Array<{ x: number; y: number }> {
    const order: string[][] = [['cashier'], ['payment'], ['exit']];
    const out: Array<{ x: number; y: number }> = [];
    for (const types of order) {
      for (let i = 0; i < lot.tiles.length; i++) {
        if (types.includes(lot.tiles[i])) {
          out.push({ x: i % lot.width, y: Math.floor(i / lot.width) });
        }
      }
    }
    return out;
  }

  private roleHome(lot: ParkingLot, kind: string): { x: number; y: number } {
    const cx = lot.width / 2;
    const cy = lot.height / 2;
    if (kind === 'cashier') {
      return (
        this.nearestTileOf(lot, cx, cy, ['cashier', 'payment']) ??
        this.nearestTileOf(lot, cx, cy, ['exit']) ?? { x: cx, y: cy }
      );
    }
    if (kind === 'manager') {
      return this.nearestTileOf(lot, cx, cy, ['office', 'entrance']) ?? { x: cx, y: cy };
    }
    return { x: cx, y: cy };
  }

  private pickPedTarget(lot: ParkingLot, ped: Ped): void {
    if (ped.kind === 'driver') {
      // Walk out to the destination, then back toward the car, repeating.
      if (ped.goingOut && ped.destX !== undefined && ped.destY !== undefined) {
        ped.tx = ped.destX;
        ped.ty = ped.destY;
      } else {
        ped.tx = ped.homeX;
        ped.ty = ped.homeY;
      }
      ped.goingOut = !ped.goingOut;
      return;
    }
    if (ped.kind === 'cashier') {
      // Stationed at the booth — stay put.
      ped.tx = ped.homeX;
      ped.ty = ped.homeY;
      return;
    }
    if (ped.kind === 'manager') {
      // Near the office with a small idle shuffle.
      ped.tx = this.clampTile(ped.homeX + (Math.random() - 0.5) * 1.5, lot.width);
      ped.ty = this.clampTile(ped.homeY + (Math.random() - 0.5) * 1.5, lot.height);
      return;
    }
    // cleaner / security / technician patrol anywhere.
    ped.tx = this.randInt(lot.width);
    ped.ty = this.randInt(lot.height);
  }

  private clampTile(v: number, dim: number): number {
    return Math.max(0, Math.min(dim - 1, v));
  }

  private updatePedestrians(lot: ParkingLot, delta: number): void {
    const now = this.time.now;
    const seen = new Set<string>();

    // Cashiers man booths: distribute them across cashier/machine/exit stations.
    const stations = this.cashierStations(lot);
    let cashierIdx = 0;

    // One ped per staff member.
    for (const s of lot.staff) {
      const key = `staff-${s.id}`;
      seen.add(key);
      if (!this.peds.has(key)) {
        let home: { x: number; y: number };
        if (s.typeId === 'cashier' && stations.length > 0) {
          home = stations[cashierIdx % stations.length];
        } else {
          home = this.roleHome(lot, s.typeId);
        }
        const ped = this.createPed(s.typeId, home.x, home.y);
        ped.homeX = home.x;
        ped.homeY = home.y;
        this.peds.set(key, ped);
      }
      if (s.typeId === 'cashier') cashierIdx++;
    }

    // Drivers stay in their cars (they pay at the exit) — only staff walk.

    // Move peds; remove any whose owner is gone.
    for (const [key, ped] of this.peds) {
      if (!seen.has(key)) {
        ped.container.destroy();
        this.peds.delete(key);
        continue;
      }
      this.movePed(lot, ped, delta, now);
      const { px, py } = this.tileCenterPx(ped.x, ped.y);
      ped.container.setPosition(px, py);
    }
  }

  private movePed(lot: ParkingLot, ped: Ped, delta: number, now: number): void {
    if (now < ped.idleUntilMs) return;
    const step = (ped.speed * delta) / 1000;
    const dx = ped.tx - ped.x;
    const dy = ped.ty - ped.y;
    const d = Math.hypot(dx, dy);
    if (d <= step || d < 1e-3) {
      ped.x = ped.tx;
      ped.y = ped.ty;
      ped.idleUntilMs = now + 300 + Math.random() * 1200;
      this.pickPedTarget(lot, ped);
    } else {
      ped.x += (dx / d) * step;
      ped.y += (dy / d) * step;
    }
  }

  /** Floating "+฿fee" text that rises and fades — the cashier collecting money. */
  private showPaymentPopup(x: number, y: number, fee: number): void {
    const label = this.add
      .text(x, y - this.tileSize * 0.4, `+฿${fee}`, {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(13),
        color: '#a5d6a7',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(60);
    this.tweens.add({
      targets: label,
      y: y - this.tileSize * 1.4,
      alpha: 0,
      duration: 1100,
      ease: 'Cubic.easeOut',
      onComplete: () => label.destroy(),
    });
  }

  private flashInfo(message: string): void {
    const prevColor = '#b0bec5';
    this.infoText.setColor('#ff8a80').setText(message);
    this.time.delayedCall(900, () => {
      this.infoText.setColor(prevColor);
      this.refresh();
    });
  }
}
